/**
 * uploadOcrDocument — sube un documento (PDF o imagen) al pipeline OCR async
 * para que ARIA lo clasifique y proponga crear gasto/factura.
 *
 * Tres pasos (mismo flujo que OcrScanModal pero por la vía asíncrona SQS, que
 * escala para bulk y no bloquea el Lambda):
 *   1. POST /api/functions/presigned-upload → { uploadUrl, key }
 *   2. PUT directo a S3 (sin tope de payload del Function URL → admite PDF).
 *   3. POST /api/ocr-scans (registro) + POST /api/ocr-scans/:id/process (encola
 *      el worker). Devuelve el scan_id; el estado se sigue con useOcrBatchStatus.
 *
 * SEC-06: el backend valida que el file_url pertenece al tenant (beforeCreate de
 * ocrScans) y todas las queries del worker filtran por company_id.
 */

import { apiFetch, unwrapOne } from '@/utils/api';

// Tipos admitidos: PDF + imágenes que Bedrock vision procesa. HEIC/HEIF
// (fotos de iPhone) se aceptan en la UI pero se CONVIERTEN a JPEG en el
// navegador antes de subir (Bedrock vision no soporta HEIC).
export const OCR_ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
];

// Tope defensivo por archivo (10 MB) — coincide con el límite del OcrScanModal.
export const OCR_MAX_BYTES = 10 * 1024 * 1024;

// HEIC/HEIF — el navegador a veces reporta type vacío, así que también miramos
// la extensión del nombre.
function isHeic(file) {
  return /image\/hei[cf]/i.test(file?.type || '') || /\.hei[cf]$/i.test(file?.name || '');
}

export function isAcceptedOcrFile(file) {
  if (!file || file.size > OCR_MAX_BYTES) return false;
  return OCR_ACCEPTED_TYPES.includes(file.type) || isHeic(file);
}

// Convierte un HEIC/HEIF a JPEG en el navegador (lazy import de heic2any para no
// inflar el bundle de quien no sube fotos de iPhone). Devuelve un File JPEG.
async function convertHeicToJpeg(file) {
  const { default: heic2any } = await import('heic2any');
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const out = Array.isArray(blob) ? blob[0] : blob;
  const name = file.name.replace(/\.hei[cf]$/i, '.jpg') || 'foto.jpg';
  return new File([out], name, { type: 'image/jpeg' });
}

/**
 * Sube un File y dispara su procesamiento OCR.
 * @param {File} file
 * @param {{ userId?: string, source?: string }} [opts] `source` etiqueta el origen
 *        del scan (`ocr_scans.source`); por defecto 'aria_chat' para no alterar a
 *        los llamadores existentes. La Bandeja pasa 'inbox_manual'.
 * @returns {Promise<{ scanId: string, name: string, fileUrl: string, fileType: string }>}
 */
export async function uploadOcrDocument(file, opts = {}) {
  if (!isAcceptedOcrFile(file)) {
    throw new Error('Formato no admitido o archivo demasiado grande (máx. 10 MB). Usa PDF, JPG, PNG, WebP, GIF o HEIC.');
  }

  // HEIC → JPEG antes de subir (Bedrock vision no soporta HEIC).
  let upload = file;
  if (isHeic(file)) {
    try {
      upload = await convertHeicToJpeg(file);
    } catch {
      throw new Error('No se pudo convertir la imagen HEIC. Prueba a exportarla como JPG.');
    }
  }

  // 1. Presigned URL.
  const presigned = await apiFetch('/api/functions/presigned-upload', {
    method: 'POST',
    body: JSON.stringify({ file_name: upload.name, file_type: upload.type }),
  });
  const uploadUrl = presigned?.uploadUrl;
  const key = presigned?.key;
  if (!uploadUrl || !key) throw new Error('No se pudo preparar la subida del archivo.');

  // 2. PUT directo a S3.
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': upload.type },
    body: upload,
  });
  if (!putRes.ok) throw new Error('Falló la subida del archivo a almacenamiento.');

  // 3. Registro + encolado del worker (vía asíncrona).
  const scan = unwrapOne(await apiFetch('/api/ocr-scans', {
    method: 'POST',
    body: JSON.stringify({
      ...(opts.userId ? { user_id: opts.userId } : {}),
      file_url: key,
      file_name: upload.name,
      file_type: upload.type,
      file_size: upload.size,
      status: 'pending',
      source: opts.source || 'aria_chat',
    }),
  }));
  if (!scan?.id) throw new Error('No se pudo registrar el documento.');

  await apiFetch(`/api/ocr-scans/${scan.id}/process`, { method: 'POST' });

  return { scanId: scan.id, name: upload.name, fileUrl: key, fileType: upload.type };
}
